/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React, { ReactNode } from "react";

import Typography from "@mui/material/Typography";

import { ExpandLessIcon } from "@dotnet/telematics-design-system";

import { TABLE_NAMES } from "../../../../../shared";
import RectangleLoader from "../../../../../shared/components/loaders/RectangleLoader";
import InfoIconWithTooltip from "../../../../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import OptionalTooltip from "../../../../Tooltip/OptionalTooltip";
import { useStyles } from "../../../styles";
import { ExpandIconPropsType } from "../../../types";
import OpenFilesButton from "../components/OpenFilesButton/OpenFilesButton";

const useDetailsCard = ({ ...props }) => {
  const {
    colors,
    t,
    cardTitle,
    infoIcon,
    infoTooltip,
    inlineSubtitle,
    collapsable,
    moreActions,
    detailsData
  } = props;

  const { collapseArrow, collapsableTitleArea, paperCardTitleWithInfo } =
    useStyles();

  const renderInlineSubtitle = (inlineSubtitle: string | undefined) =>
    inlineSubtitle ? (
      <Typography
        variant="body2Medium"
        sx={{
          color: colors.textSecondary,
          marginRight: "16px",
          cursor: "default"
        }}
      >
        {inlineSubtitle}
      </Typography>
    ) : null;

  const renderExpandIcon = (props: ExpandIconPropsType) => {
    return (
      <div css={css(collapseArrow)} onClick={props.handleToggleCollapse}>
        {
          <OptionalTooltip
            tooltipTitle={t(
              props.isOpen ? "Actions##collapse" : "Actions##expand"
            )}
            isDisabled={!props.showExpandTooltip}
            tooltipPlacement="top"
          >
            <ExpandLessIcon
              data-testid="expand-less-icon"
              css={css({
                transition: `all ${props.transitionTiming} ease`,
                transform: `rotate(${props.arrowRotationAngle})`
              })}
            />
          </OptionalTooltip>
        }
      </div>
    );
  };

  const renderTitleArea = (
    renderIcon: () => React.JSX.Element,
    resourceName: TABLE_NAMES | undefined
  ) => (
    <div css={css(collapsableTitleArea)}>
      <Typography
        variant="body2SemiBold"
        sx={infoIcon && (paperCardTitleWithInfo as any)}
      >
        {cardTitle}
        {infoIcon && (
          <span>
            <InfoIconWithTooltip title={infoTooltip || ""} />
          </span>
        )}
      </Typography>
      <div css={css(collapseArrow)}>
        {Boolean(inlineSubtitle) && renderInlineSubtitle(inlineSubtitle)}
        {collapsable && renderIcon()}
      </div>
      {moreActions || (
        <OpenFilesButton
          detailsData={detailsData}
          resourceName={resourceName}
        />
      )}
    </div>
  );

  const renderChildren = (displayLoader: boolean, children: ReactNode) =>
    displayLoader ? (
      <RectangleLoader
        height="16px"
        width="129px"
        testId={`details-card-content-loader-${cardTitle}`}
      />
    ) : (
      children
    );

  return {
    renderInlineSubtitle,
    renderExpandIcon,
    renderTitleArea,
    renderChildren
  };
};

export default useDetailsCard;
